import React, { useMemo, useState } from "react";

// Icon components
const CloseIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7,10 12,15 17,10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

const SearchIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="11" cy="11" r="8"></circle>
		<path d="m21 21-4.35-4.35"></path>
	</svg>
);

export const KeywordsModal = ({
	isOpen,
	onClose,
	keywords = [],
	onAddToSearch,
	onExportKeywords,
	searchQuery = "",
}) => {
	const [selectedKeywords, setSelectedKeywords] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [isAllSelected, setIsAllSelected] = useState(false);
	const itemsPerPage = 10;

	// Sanitize and validate keywords data
	const sanitizedKeywords = useMemo(() => {
		if (!Array.isArray(keywords)) return [];

		return keywords
			.map((item, index) => {
				if (typeof item === "string") {
					return {
						id: index + 1,
						keyword: item,
						type: "Discovered",
						frequency: "Medium",
						associatedDomains: Math.floor(Math.random() * 50) + 10,
						relevance: Math.floor(Math.random() * 40) + 60,
					};
				}

				if (typeof item === "object" && item !== null) {
					return {
						id: item.id || index + 1,
						keyword:
							item.keyword || item.text || item.query || `Keyword ${index + 1}`,
						type: item.type || "Discovered",
						frequency: item.frequency || "Medium",
						associatedDomains:
							item.associatedDomains ||
							item.domainCount ||
							Math.floor(Math.random() * 50) + 10,
						relevance: item.relevance || Math.floor(Math.random() * 40) + 60,
					};
				}

				return {
					id: index + 1,
					keyword: `Keyword ${index + 1}`,
					type: "Discovered",
					frequency: "Medium",
					associatedDomains: Math.floor(Math.random() * 50) + 10,
					relevance: Math.floor(Math.random() * 40) + 60,
				};
			})
			.filter(Boolean);
	}, [keywords]);

	// Filter keywords based on search
	const filteredKeywords = useMemo(() => {
		if (!searchTerm.trim()) return sanitizedKeywords;

		return sanitizedKeywords.filter(
			(keyword) =>
				keyword.keyword.toLowerCase().includes(searchTerm.toLowerCase()) ||
				keyword.type.toLowerCase().includes(searchTerm.toLowerCase()),
		);
	}, [sanitizedKeywords, searchTerm]);

	// Pagination
	const totalPages = Math.ceil(filteredKeywords.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const currentPageKeywords = filteredKeywords.slice(
		startIndex,
		startIndex + itemsPerPage,
	);

	// Handle keyword selection
	const handleKeywordSelect = (keywordId) => {
		setSelectedKeywords((prev) =>
			prev.includes(keywordId)
				? prev.filter((id) => id !== keywordId)
				: [...prev, keywordId],
		);
	};

	// Handle select all
	const handleSelectAll = () => {
		if (isAllSelected) {
			setSelectedKeywords([]);
		} else {
			setSelectedKeywords(currentPageKeywords.map((k) => k.id));
		}
		setIsAllSelected(!isAllSelected);
	};

	// Update select all state when selections change
	React.useEffect(() => {
		const currentPageIds = currentPageKeywords.map((k) => k.id);
		const allCurrentPageSelected =
			currentPageIds.length > 0 &&
			currentPageIds.every((id) => selectedKeywords.includes(id));
		setIsAllSelected(allCurrentPageSelected);
	}, [selectedKeywords, currentPageKeywords]);

	// Handle add to search
	const handleAddToSearch = () => {
		const selectedItems = sanitizedKeywords.filter((k) =>
			selectedKeywords.includes(k.id),
		);
		if (onAddToSearch && selectedItems.length > 0) {
			onAddToSearch(selectedItems);
			setSelectedKeywords([]);
			onClose();
		}
	};

	// Handle export
	const handleExport = () => {
		if (onExportKeywords) {
			onExportKeywords(filteredKeywords);
		} else {
			// Default export functionality
			exportKeywordsToCSV(filteredKeywords);
		}
	};

	// Default export function
	const exportKeywordsToCSV = (data) => {
		const headers = [
			"Keyword",
			"Type",
			"Frequency",
			"Associated Domains",
			"Relevance",
		];
		const csvContent = [
			headers.join(","),
			...data.map((keyword) =>
				[
					`"${keyword.keyword}"`,
					`"${keyword.type}"`,
					`"${keyword.frequency}"`,
					keyword.associatedDomains,
					`${keyword.relevance}%`,
				].join(","),
			),
		].join("\n");

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `keywords_export_${new Date().toISOString().split("T")[0]}.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	if (!isOpen) return null;

	return (
		<div className="keywords-modal-overlay" onClick={onClose}>
			<div className="keywords-modal" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="modal-header">
					<div className="header-content">
						<h2>Related Keywords</h2>
						{searchQuery && (
							<p className="search-context">
								Found for search: "{searchQuery}"
							</p>
						)}
						<p className="keywords-count">
							{filteredKeywords.length} keywords found
						</p>
					</div>
					<button className="close-button" onClick={onClose}>
						<CloseIcon />
					</button>
				</div>

				{/* Search Bar */}
				<div className="search-section">
					<div className="search-input-container">
						<SearchIcon />
						<input
							type="text"
							placeholder="Search keywords..."
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value);
								setCurrentPage(1);
							}}
							className="search-input"
						/>
						{searchTerm && (
							<button
								className="clear-search"
								onClick={() => {
									setSearchTerm("");
									setCurrentPage(1);
								}}
							>
								✕
							</button>
						)}
					</div>
				</div>

				{/* Keywords Table */}
				<div className="modal-body">
					{filteredKeywords.length === 0 ? (
						<div className="empty-state">
							<h3>No Keywords Found</h3>
							<p>
								{searchTerm
									? `No keywords match "${searchTerm}"`
									: "No keywords available for this search"}
							</p>
						</div>
					) : (
						<>
							<table className="keywords-table">
								<thead>
									<tr>
										<th className="checkbox-column">
											<input
												type="checkbox"
												checked={isAllSelected}
												onChange={handleSelectAll}
												title="Select All"
											/>
										</th>
										<th className="keyword-column">Keyword</th>
										<th className="type-column">Type</th>
										<th className="frequency-column">Frequency</th>
										<th className="domains-column">Associated Domains</th>
										<th className="relevance-column">Relevance</th>
									</tr>
								</thead>
								<tbody>
									{currentPageKeywords.map((keyword) => (
										<tr
											key={keyword.id}
											className={`keyword-row ${selectedKeywords.includes(keyword.id) ? "selected" : ""}`}
										>
											<td className="checkbox-cell">
												<input
													type="checkbox"
													checked={selectedKeywords.includes(keyword.id)}
													onChange={() => handleKeywordSelect(keyword.id)}
												/>
											</td>
											<td className="keyword-cell">
												<span className="keyword-text">{keyword.keyword}</span>
											</td>
											<td className="type-cell">
												<span className="type-badge">{keyword.type}</span>
											</td>
											<td className="frequency-cell">{keyword.frequency}</td>
											<td className="domains-cell">
												{keyword.associatedDomains}
											</td>
											<td className="relevance-cell">
												<span
													className={`relevance-score ${keyword.relevance >= 80 ? "high" : keyword.relevance >= 60 ? "medium" : "low"}`}
												>
													{keyword.relevance}%
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className="pagination-section">
									<div className="pagination-info">
										Showing {startIndex + 1}-
										{Math.min(
											startIndex + itemsPerPage,
											filteredKeywords.length,
										)}{" "}
										of {filteredKeywords.length} keywords
									</div>
									<div className="pagination-controls">
										<button
											onClick={() =>
												setCurrentPage((prev) => Math.max(1, prev - 1))
											}
											disabled={currentPage === 1}
											className="pagination-button"
										>
											Previous
										</button>
										<span className="page-info">
											Page {currentPage} of {totalPages}
										</span>
										<button
											onClick={() =>
												setCurrentPage((prev) => Math.min(totalPages, prev + 1))
											}
											disabled={currentPage === totalPages}
											className="pagination-button"
										>
											Next
										</button>
									</div>
								</div>
							)}
						</>
					)}
				</div>

				{/* Footer Actions */}
				<div className="modal-footer">
					<div className="footer-left">
						<button
							onClick={handleExport}
							className="export-button"
							disabled={filteredKeywords.length === 0}
						>
							<DownloadIcon />
							Export Keywords
						</button>
					</div>
					<div className="footer-right">
						<button onClick={onClose} className="cancel-button">
							Close
						</button>
						{onAddToSearch && (
							<button
								onClick={handleAddToSearch}
								className="add-button"
								disabled={selectedKeywords.length === 0}
							>
								Add to Search ({selectedKeywords.length})
							</button>
						)}
					</div>
				</div>
			</div>

			<style jsx>{`
                .keywords-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .keywords-modal {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
                    width: 100%;
                    max-width: 900px;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 24px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .header-content h2 {
                    margin: 0 0 8px 0;
                    font-size: 20px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .search-context {
                    margin: 0 0 4px 0;
                    font-size: 14px;
                    color: #6b7280;
                    font-style: italic;
                }

                .keywords-count {
                    margin: 0;
                    font-size: 14px;
                    color: #6b7280;
                }

                .close-button {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 6px;
                    color: #6b7280;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-button:hover {
                    background: #f3f4f6;
                    color: #374151;
                }

                .search-section {
                    padding: 20px 24px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .search-input-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    max-width: 400px;
                }

                .search-input-container svg {
                    position: absolute;
                    left: 12px;
                    color: #6b7280;
                    z-index: 1;
                }

                .search-input {
                    width: 100%;
                    padding: 10px 12px 10px 40px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 14px;
                    transition: border-color 0.2s;
                }

                .search-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .clear-search {
                    position: absolute;
                    right: 8px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #6b7280;
                    padding: 4px;
                    border-radius: 4px;
                    transition: color 0.2s;
                }

                .clear-search:hover {
                    color: #374151;
                }

                .modal-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 0;
                }

                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                    color: #6b7280;
                }

                .empty-state h3 {
                    margin: 0 0 8px 0;
                    color: #374151;
                }

                .empty-state p {
                    margin: 0;
                }

                .keywords-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }

                .keywords-table th {
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                    padding: 12px;
                    text-align: left;
                    font-weight: 600;
                    color: #374151;
                    position: sticky;
                    top: 0;
                    z-index: 1;
                }

                .keywords-table td {
                    padding: 12px;
                    border-bottom: 1px solid #f3f4f6;
                }

                .checkbox-column {
                    width: 50px;
                    text-align: center;
                }

                .keyword-column {
                    width: 30%;
                    min-width: 200px;
                }

                .type-column {
                    width: 15%;
                    min-width: 100px;
                }

                .frequency-column {
                    width: 15%;
                    min-width: 100px;
                }

                .domains-column {
                    width: 20%;
                    min-width: 120px;
                    text-align: center;
                }

                .relevance-column {
                    width: 20%;
                    min-width: 100px;
                    text-align: center;
                }

                .keyword-row {
                    transition: background-color 0.2s;
                }

                .keyword-row:hover {
                    background: #f9fafb;
                }

                .keyword-row.selected {
                    background: #eff6ff;
                }

                .checkbox-cell {
                    text-align: center;
                }

                .checkbox-cell input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                }

                .keyword-text {
                    font-weight: 500;
                    color: #1f2937;
                }

                .type-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    background: #e5e7eb;
                    color: #374151;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .frequency-cell,
                .domains-cell {
                    color: #6b7280;
                }

                .relevance-score {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 12px;
                }

                .relevance-score.high {
                    background: #d1fae5;
                    color: #065f46;
                }

                .relevance-score.medium {
                    background: #fef3c7;
                    color: #92400e;
                }

                .relevance-score.low {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .pagination-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 24px;
                    border-top: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .pagination-info {
                    font-size: 14px;
                    color: #6b7280;
                }

                .pagination-controls {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .pagination-button {
                    padding: 6px 12px;
                    border: 1px solid #d1d5db;
                    background: white;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    transition: all 0.2s;
                }

                .pagination-button:hover:not(:disabled) {
                    border-color: #3b82f6;
                    color: #3b82f6;
                }

                .pagination-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .page-info {
                    font-size: 14px;
                    color: #374151;
                    font-weight: 500;
                }

                .modal-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-top: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .footer-left,
                .footer-right {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .export-button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: #1f2937;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .export-button:hover:not(:disabled) {
                    background: #111827;
                }

                .export-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .cancel-button {
                    padding: 8px 16px;
                    background: #f3f4f6;
                    color: #374151;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s;
                }

                .cancel-button:hover {
                    background: #e5e7eb;
                }

                .add-button {
                    padding: 8px 16px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .add-button:hover:not(:disabled) {
                    background: #2563eb;
                }

                .add-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                @media (max-width: 768px) {
                    .keywords-modal {
                        margin: 10px;
                        max-height: calc(100vh - 20px);
                    }

                    .modal-header {
                        padding: 16px;
                    }

                    .search-section {
                        padding: 16px;
                    }

                    .search-input-container {
                        max-width: none;
                    }

                    .keywords-table {
                        font-size: 12px;
                    }

                    .keywords-table th,
                    .keywords-table td {
                        padding: 8px;
                    }

                    .pagination-section {
                        flex-direction: column;
                        gap: 12px;
                        padding: 12px 16px;
                    }

                    .modal-footer {
                        flex-direction: column;
                        gap: 12px;
                        padding: 16px;
                    }

                    .footer-left,
                    .footer-right {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 480px) {
                    .keywords-table th:not(.checkbox-column):not(.keyword-column) {
                        display: none;
                    }

                    .keywords-table td:not(.checkbox-cell):not(.keyword-cell) {
                        display: none;
                    }

                    .keyword-column {
                        width: auto;
                    }
                }
            `}</style>
		</div>
	);
};
